import axios from 'axios'
import { useException } from '@/composables/exceptions'
import { useWebSiteStore } from '@/stores/web-site.store'
import { invalidateKpis } from '@/composables/kpi-refresh'

const MUTATING_METHODS = ['post', 'put', 'patch', 'delete']

// const CSRF_COOKIE_NAME = 'csrftoken'
// const CSRF_HEADER_NAME = 'X-CSRFToken'

const httpApi = axios.create({
  baseURL: window.config.BASE_API,
  timeout: window.config.TIMEOUT,
})

httpApi.firstTime = true

// // Request Interceptor
httpApi.interceptors.request.use(config => {
  return config
}, error => {
  // showMessage(error, messageType.ERROR)
})

// Response Interceptor
httpApi.interceptors.response.use(
  response => {
    // Any successful mutation may change the numbers behind the contextual KPIs → refetch them.
    if (MUTATING_METHODS.includes(response.config?.method?.toLowerCase())) invalidateKpis()
    return response
  },
  async(error) => {
    // console.error(error)
    const { processError } = useException()
    const originalRequest = error.config

    if (originalRequest._isRetrying) return Promise.reject(error)

    // Handle blob error responses
    if (error.response && error.response.config.responseType === 'blob') {
      return new Promise((resolve, reject) => {
        const blb = new Blob([error.response.data])
        const reader = new FileReader()
        reader.readAsText(blb)
        reader.addEventListener('loadend', (e) => {
          error.response.data = JSON.parse(e.srcElement?.result || '{}')
          if (!error.response.config.skipErrorMessage) processError(error)
          reject(error)
        })
      })
    }

    if (error.response?.status === 401 && !originalRequest._isRefreshCall) {
      originalRequest._isRetrying = true
      const webSiteStore = useWebSiteStore()
      try {
        await webSiteStore.refreshTokenAsync({ _isRetrying: true })
        originalRequest.headers['Authorization'] = `Bearer ${webSiteStore.accessToken}`
        return httpApi(originalRequest)
      } catch (refreshErr) {
        await webSiteStore.logoutAsync()
        return Promise.reject(refreshErr)
      }
    }

    if (!error.response?.config?.skipErrorMessage) processError(error)
    return Promise.reject(error)
  }
)

export const setCommonHeader = (header, value) => {
  httpApi.defaults.headers.common[header] = value
}

export const setUserDataFilterHeader = (value) => setCommonHeader('X-User-Data-Filter', value)

export const setAuthorizationToken = (token) => {
  !token ? setCommonHeader('Authorization', null) : setCommonHeader('Authorization', `Bearer ${token}`)
}

export const setAcceptLanguageHeader = (language) => {
  httpApi.defaults.headers.common['Accept-Language'] = language
}

export const setTimezoneHeader = (tz) => {
  if (tz === null) {
    delete httpApi.defaults.headers.common['x-timezone']
  } else {
    httpApi.defaults.headers.common['x-timezone'] = tz || Intl.DateTimeFormat().resolvedOptions().timeZone
  }
}

export default httpApi
